import { Injectable, signal } from '@angular/core';
import { WorkforceBlockedSiteDto } from 'src/shared/services/api.service';

/**
 * Shell-scoped record of the selected LEA's sites whose invalid contacts or missing Title I status make the
 * server reject workforce changes there.
 */
@Injectable()
export class WorkforceSiteDataState {
  private readonly blockedSites = signal<ReadonlyMap<number, WorkforceBlockedSiteDto>>(new Map());

  setBlockedSites(sites: WorkforceBlockedSiteDto[]): void {
    this.blockedSites.set(new Map(sites.map((site) => [site.leaSiteCodeId, site])));
  }

  /** Returns why workforce changes at the site are unavailable, or null when they are allowed. */
  blockedReason(leaSiteCodeId: number | null | undefined): string | null {
    const site =
      leaSiteCodeId === null || leaSiteCodeId === undefined ? undefined : this.blockedSites().get(leaSiteCodeId);
    if (!site) return null;

    return site.invalidContacts
      ? 'Location is missing required contacts. Workforce changes are unavailable until contacts are corrected.'
      : 'Location is missing a Title I Status. Workforce changes are unavailable until it is set.';
  }

  isBlocked(leaSiteCodeId: number | null | undefined): boolean {
    return this.blockedReason(leaSiteCodeId) !== null;
  }
}

export type WorkforceSiteAvailability<T> = T & { disabled: boolean; blockedReason: string | null };

/** Marks options at blocked sites as disabled (ng-select honors `disabled`) and attaches the reason. */
export function withSiteAvailability<T extends object>(
  items: readonly T[],
  siteId: (item: T) => number | null | undefined,
  state: WorkforceSiteDataState | null,
): WorkforceSiteAvailability<T>[] {
  return items.map((item) => {
    const blockedReason = state?.blockedReason(siteId(item)) ?? null;
    return { ...item, disabled: blockedReason !== null, blockedReason };
  });
}
